import { useState, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';

import DirectReportSelect from '../../../../components/DirectReportSelect/DirectReportSelect';
import { getDirectReports } from '../_api';
import Alert from '@mui/material/Alert';
import DirectReportsSelections from '../../../../components/DirectReportSelect/components/DirectReportsSelections';
import { nanoid } from 'nanoid';
import ReviewInformation from './steps/ReviewInformation/ReviewInformation';
import SubmissionSummary from './steps/SubmissionSummary';
import { submitTerminations } from '../_api';
import Spinner from '../../../../../../components/UI/Spinner/Spinner';
import { getDefaultTerminationDate } from '../_utils';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import styles from '../TerminateEmployeeAccess.module.scss';
import MaterialIcon from '@material/react-material-icon';
import { PAGE_ICONS, PAGE_ROUTES } from '../../../../_const';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import HomeIcon from '@mui/icons-material/Home';

import { fetchBasicStaffList } from 'pages/AnnualUserAccessReview/_api';
import { processStaffDataForTable } from 'pages/AnnualUserAccessReview/_util';
const steps = [
	'Select Direct Reports',
	'Review Information',
	'Submission Summary',
];
const TerminateEmployeeStepper = ({
	formik,
	currentUser,
	loggedInUser,
	targetUser,
	messages = [],
}) => {
	const [activeStep, setActiveStep] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [directReports, setDirectReports] = useState([]);
	const [universalTerminationDate, setUniversalTerminationDate] = useState(
		getDefaultTerminationDate()
	);
	const [terminateAsap, setTerminateAsap] = useState(false);
	const history = useHistory();

	const disableSubmit = useMemo(() => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const checkDate = new Date(universalTerminationDate);
		checkDate.setHours(0, 0, 0, 0);
		//check if date is more than 1 year from today
		const nextYear = new Date();
		nextYear.setFullYear(today.getFullYear() + 1);
		nextYear.setHours(0, 0, 0, 0);
		if (checkDate > nextYear || today > checkDate) {
			return true;
		} else {
			return false;
		}
	}, [universalTerminationDate]);

	useEffect(() => {
		setIsLoading(true);
		let impersonationID = currentUser.NetworkID;
		getDirectReports({
			upn: loggedInUser.UPN,
			impersonationId: impersonationID,
		}).then(data => {
			setDirectReports(
				data?.data?.payload?.staff.sort((a, b) => {
					if (a.lastName < b.lastName) {
						return -1;
					} else {
						return 1;
					}
				})
			);

			setIsLoading(false);
		});
	}, [currentUser, loggedInUser]);

	const handleNext = () => {
		if (
			Object.keys(formik.errors).length === 0 &&
			formik.touched.staffMembersForReview
		) {
			setActiveStep(prevActiveStep => prevActiveStep + 1);
		}
	};

	const handleBack = () => {
		setActiveStep(prevActiveStep => prevActiveStep - 1);
	};

	const handleReset = () => {
		//reset formik
		formik.setFieldValue('staffMembersForReview', []);
		formik.setTouched({
			...formik.touched,
			staffMembersForReview: false,
		});
		// //reset page back to initial step
		// setActiveStep(0);
		setActiveStep(0);

		//refresh direct reports options
		setIsLoading(true);
		let impersonationID = currentUser.NetworkID;
		getDirectReports({
			upn: loggedInUser.UPN,
			impersonationId: impersonationID,
		}).then(data => {
			setDirectReports(
				data?.data?.payload?.staff.sort((a, b) => {
					if (a.lastName < b.lastName) {
						return -1;
					} else {
						return 1;
					}
				})
			);

			setIsLoading(false);
		});
	};
	const [sStaff, setStaff] = useState([]);
	useEffect(() => {
		let active = true;

		async function fetchApi() {
			//fetch the base job category info
			const response = await fetchBasicStaffList(targetUser.ID);
			const { values } = response;
			const data = processStaffDataForTable(values);
			//set the state
			setStaff(data);
			setIsLoading(false);
		}

		if (active && targetUser.ID) {
			fetchApi();
		}

		return () => {
			active = false;
		};
	}, [targetUser]);

	const renderActiveStep = () => {
		switch (activeStep) {
			case 0:
				return (
					<DirectReportSelect
						key={'step_' + activeStep}
						name='staffMembersForReview'
						formik={formik}
						directReports={sStaff}
						headlineText='Select staff for Termination'
					/>
				);
			case 1:
				return (
					<ReviewInformation
						formik={formik}
						universalTerminationDate={universalTerminationDate}
						setUniversalTerminationDate={
							setUniversalTerminationDate
						}
						terminateAsap={terminateAsap}
						setTerminateAsap={setTerminateAsap}
					/>
				);
			case 2:
				return (
					<SubmissionSummary
						staffMembers={formik.values.staffMembersForReview}
					/>
				);
			default:
				return (
					<div>
						Error Occurred... no defined step for step #{activeStep}
					</div>
				);
		}
	};

	const StepperControls = props => {
		switch (props.activeStep) {
			case 0:
				return (
					<>
						<Button
							disabled={activeStep === 0}
							onClick={handleBack}
							sx={{ mr: 2, mb: 5 }}
							variant='contained'
						>
							{'<<'} Back
						</Button>
						<Button
							onClick={() => {
								formik.setTouched({
									...formik.touched,
									staffMembersForReview: true,
								});
								handleNext();
							}}
							sx={{ mb: 5 }}
							variant='contained'
						>
							Review {'>>'}
						</Button>
					</>
				);
			case 1:
				return (
					<>
						<Button
							disabled={activeStep === 0}
							onClick={handleBack}
							sx={{ mr: 2, mb: 5 }}
							variant='contained'
						>
							{'<<'} Back
						</Button>
						<TerminateButton
							formik={formik}
							handleNext={handleNext}
							setIsLoading={setIsLoading}
							isLoading={isLoading}
							terminationDate={universalTerminationDate}
							terminateAsap={terminateAsap}
							disabled={disableSubmit}
						/>
					</>
				);
			default:
				return (
					<>
						<Button
							variant='contained'
							startIcon={<PAGE_ICONS.HOME />}
							onClick={() => history.push(PAGE_ROUTES.HOME)}
							sx={{ mr: 2, mb: 5 }}
						>
							Back to User Access Review Home Page
						</Button>
						<Button
							variant='contained'
							startIcon={<HomeIcon />}
							onClick={() => history.push('/index.html')}
							sx={{ mr: 2, mb: 5 }}
						>
							Back to IMS Home Page
						</Button>
					</>
				);
		}
	};

	return (
		<Box sx={{ width: '100%' }}>
			<>
				{messages.map(({ type, message }) => {
					return <Alert severity={type}>{message}</Alert>;
				})}
			</>
			<Stepper activeStep={activeStep}>
				{steps.map(label => {
					const stepProps = {};
					const labelProps = {};
					return (
						<Step key={label} {...stepProps}>
							<StepLabel {...labelProps}>{label}</StepLabel>
						</Step>
					);
				})}
			</Stepper>

			{activeStep === steps.length ? (
				<>
					<Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
						<Box sx={{ flex: '1 1 auto' }} />
						<Button onClick={handleReset}>Reset</Button>
					</Box>
				</>
			) : (
				<>
					<Box sx={{ mt: 3 }}>
						{Object.keys(formik.errors).length > 0 &&
						Object.keys(formik.touched).length > 0 ? (
							<Alert severity='error'>
								<ul>
									{Object.keys(formik.errors).map(error => {
										return (
											<li key={nanoid()}>
												{formik.errors[error]}
											</li>
										);
									})}
								</ul>
							</Alert>
						) : null}
						<DirectReportsSelections
							formik={formik}
							selected={formik.values.staffMembersForReview}
							setSelected={selections =>
								formik.setFieldValue('staffMembersForReview', [
									...selections,
								])
							}
							directReports={directReports}
							setDirectReports={setDirectReports}
						/>

						{renderActiveStep()}
					</Box>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							pt: 2,
							justifyContent: 'center',
						}}
					>
						{isLoading ? (
							<Spinner />
						) : (
							<>
								<StepperControls activeStep={activeStep} />
							</>
						)}
					</Box>
				</>
			)}
		</Box>
	);
};

export default TerminateEmployeeStepper;

//private
const TerminateButton = ({
	formik,
	handleNext,
	setIsLoading,
	isLoading,
	terminationDate,
	terminateAsap,
	disabled,
}) => {
	const [error, setError] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);

	const confirmTerminations = () => {
		const payload = {
			loggedInUser: formik.values.currentUser,
			terminationDate: terminationDate,
			terminationTime: terminateAsap ? 'asap' : '12:00 AM',
			emailList: null, //email list is now a property of each target
			targets: formik.values.staffMembersForReview,
		};

		submitTerminations(payload)
			.then(response => {
				if (response.data.payload !== true) {
					setError(response.error);
				} else {
					handleNext();
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return (
		<>
			<Button
				onClick={() => {
					setModalOpen(!modalOpen);
				}}
				sx={{ mb: 5 }}
				variant='contained'
				disabled={disabled}
			>
				Terminate {'>>'}
			</Button>
			{error ? <Alert severity='error'>{error}</Alert> : null}
			<Modal
				open={modalOpen}
				onClose={() => {
					setModalOpen(false);
				}}
			>
				<div className={styles.Modal}>
					<TerminationConfirmation
						onConfirm={() => {
							formik.setTouched({
								...formik.touched,
								staffMembersForReview: true,
							});
							setIsLoading(true);
							confirmTerminations();
						}}
						terminationCount={
							formik.values.staffMembersForReview.length
						}
						closeModal={() => setModalOpen(false)}
						isLoading={isLoading}
					/>
				</div>
			</Modal>
		</>
	);
};

//internal

const TerminationConfirmation = ({
	onConfirm,
	terminationCount,
	closeModal,
	isLoading,
}) => {
	return (
		<>
			{isLoading ? (
				<Spinner />
			) : (
				<>
					<Typography variant='h6'>
						You are about to terminate{' '}
						<strong>{terminationCount}</strong> staff member
						{terminationCount > 1 ? <>s</> : null}. By confirming,
						you acknowledge that it is your intent to terminate the
						previously selected staff members and revoke access to
						all UPMC applications. This can not be undone.
					</Typography>
					<div className={styles.centerContent}>
						<Button
							variant='contained'
							startIcon={<MaterialIcon icon='done' />}
							onClick={() => onConfirm()}
						>
							Confirm Terminations
						</Button>
					</div>
					<div className={styles.centerContent}>
						<Button
							startIcon={<MaterialIcon icon='cancel' />}
							onClick={() => closeModal()}
						>
							Cancel
						</Button>
					</div>
				</>
			)}{' '}
		</>
	);
};
